import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import AnalyticsAPI from '../../services/api/analytics-api';

const INITIAL_VISIBLE_COUNT = 3;

/**
 * CorrectionHistorySection
 * Displays a compact, admin-only log of past timecard correction events
 * that involve the given package (as either source or destination).
 * Fetches from GET /api/analytics/packages/:packageId/corrections.
 */
const CorrectionHistorySection = ({
  packageId,
  packageName,
  token,
  refreshKey = 0,
}) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!packageId || !token) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await AnalyticsAPI.getCorrectionHistory(packageId, token);
        if (!cancelled) {
          setHistory(data?.corrections || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load correction history');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [packageId, token, refreshKey]);

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  const formatHours = (n) => {
    const num = Number(n || 0);
    return num % 1 === 0 ? num.toFixed(0) : num.toFixed(1);
  };

  const isIncoming = (row) =>
    !!packageName && row.target_package_name === packageName;

  const visibleHistory = showAll
    ? history
    : history.slice(0, INITIAL_VISIBLE_COUNT);
  const hiddenCount = Math.max(0, history.length - INITIAL_VISIBLE_COUNT);

  return (
    <Box mt={3} pt={2} sx={{ borderTop: '1px dashed #cbd5e1' }}>
      <Box display="flex" alignItems="center" gap={1} mb={1.5}>
        <HistoryIcon sx={{ fontSize: 18, color: '#64748b' }} />
        <Typography
          variant="subtitle2"
          sx={{
            color: '#475569',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          Correction History
        </Typography>
        {history.length > 0 && (
          <Chip
            size="small"
            label={history.length}
            sx={{
              height: 18,
              fontSize: 11,
              fontWeight: 600,
              bgcolor: '#e2e8f0',
              color: '#475569',
            }}
          />
        )}
      </Box>

      {loading && (
        <Box display="flex" alignItems="center" gap={1} py={1}>
          <CircularProgress size={14} />
          <Typography variant="body2" color="textSecondary">
            Loading history...
          </Typography>
        </Box>
      )}

      {error && (
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      )}

      {!loading && !error && history.length === 0 && (
        <Typography
          variant="body2"
          sx={{ color: '#94a3b8', fontStyle: 'italic' }}
        >
          No corrections recorded for this package.
        </Typography>
      )}

      {!loading && !error && history.length > 0 && (
        <>
          <Box display="flex" flexDirection="column" gap={1}>
            {visibleHistory.map((row) => {
              const incoming = isIncoming(row);
              return (
                <Box
                  key={row.correction_batch_id}
                  p={1.5}
                  sx={{
                    bgcolor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderLeft: incoming
                      ? '3px solid #10b981'
                      : '3px solid #f59e0b',
                    borderRadius: 1,
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={2}
                    mb={0.5}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: '#1e293b' }}
                      >
                        {formatDate(row.corrected_at)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        · {row.corrected_by_name || 'Unknown user'}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={incoming ? 'Moved In' : 'Moved Out'}
                      sx={{
                        height: 20,
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        bgcolor: incoming ? '#d1fae5' : '#fef3c7',
                        color: incoming ? '#065f46' : '#92400e',
                      }}
                    />
                  </Box>

                  {row.correction_reason && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#475569',
                        fontStyle: 'italic',
                        mb: 0.75,
                        lineHeight: 1.4,
                      }}
                    >
                      &ldquo;{row.correction_reason}&rdquo;
                    </Typography>
                  )}

                  <Typography
                    variant="caption"
                    sx={{ color: '#64748b', display: 'block' }}
                  >
                    <strong>{row.rows_affected}</strong>{' '}
                    {Number(row.rows_affected) === 1 ? 'entry' : 'entries'} ·{' '}
                    <strong>{formatHours(row.total_hours_moved)}h</strong>
                    {row.target_package_name && (
                      <>
                        {' '}
                        {incoming ? 'moved into' : '→ moved to'}{' '}
                        <strong>{row.target_package_name}</strong>
                      </>
                    )}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {hiddenCount > 0 && !showAll && (
            <Box mt={1}>
              <Button
                size="small"
                onClick={() => setShowAll(true)}
                endIcon={<ExpandMoreIcon />}
                sx={{
                  color: '#475569',
                  textTransform: 'none',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                Show all corrections ({hiddenCount} more)
              </Button>
            </Box>
          )}

          {showAll && history.length > INITIAL_VISIBLE_COUNT && (
            <Box mt={1}>
              <Button
                size="small"
                onClick={() => setShowAll(false)}
                sx={{
                  color: '#475569',
                  textTransform: 'none',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                Show less
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default CorrectionHistorySection;
